<script lang="ts">
  import { onMount } from 'svelte';
  import { Ingresos, Camas, Pacientes, Hospital, type Ingreso, type Cama } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import HelpHint from './HelpHint.svelte';
  import { fichaDesdeMotivo, motivoDesdeFicha } from '../lib/motivo-sync';
  import { mapaCamas, mapaNombresPaciente, etiquetaVisible, completarNombresPaciente } from '../lib/nombres';

  let items = $state<Ingreso[]>([]);
  let camas = $state<Cama[]>([]);
  let nombresPac = $state<Record<string, string>>({});
  let etiquetasCama = $state<Record<string, string>>({});
  let loading = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);

  let nuevo = $state<Ingreso>({ pacienteId: '', camaId: '', diagnosticoIngreso: '' });
  let pickerKey = $state(0);

  async function onPickPaciente(id: string) {
    nuevo.pacienteId = id;
    if (!nuevo.diagnosticoIngreso?.trim()) {
      try {
        const p = await Pacientes.uno(id);
        const mot = motivoDesdeFicha(p);
        if (mot) nuevo.diagnosticoIngreso = mot;
      } catch { /* opcional */ }
    }
  }

  function resetFormulario() {
    nuevo = { pacienteId: '', camaId: '', diagnosticoIngreso: '' };
    pickerKey += 1;
  }

  async function cargar() {
    loading = true; error = null;
    try {
      const [ing, cam, pacs] = await Promise.all([Ingresos.activos(), Camas.list(), Pacientes.list().catch(() => [])]);
      items = ing;
      camas = cam;
      nombresPac = await completarNombresPaciente(mapaNombresPaciente(pacs), items.map(i => i.pacienteId));
      etiquetasCama = mapaCamas(cam);
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crear(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevo.pacienteId) { error = 'Selecciona un paciente.'; return; }
    error = null;
    info = null;
    try {
      const body = { ...nuevo, diagnosticoIngreso: nuevo.diagnosticoIngreso?.trim() || null };
      const pacienteId = nuevo.pacienteId;
      const diag = nuevo.diagnosticoIngreso;
      await Ingresos.crear(body);
      // Limpiar de inmediato para que la pantalla no se quede con los datos del ingreso.
      resetFormulario();
      await cargar();
      info = 'Paciente ingresado.';
      if (diag?.trim() && pacienteId) {
        try {
          const partes = fichaDesdeMotivo(diag);
          const p = await Pacientes.uno(pacienteId);
          await Pacientes.actualizar(pacienteId, {
            ...p,
            motivoConsulta: partes.motivoConsulta || p.motivoConsulta,
            historiaEnfermedadActual: partes.historiaEnfermedadActual || p.historiaEnfermedadActual,
          });
        } catch { /* ficha opcional; el ingreso ya quedó */ }
      }
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  /** Tras el alta, omite dosis PENDIENTE del kárdex para que no queden fantasmas. */
  async function limpiarKardexTrasAlta(pacienteId: string) {
    try {
      const pend = await Hospital.kardexPendientes();
      const delPaciente = (Array.isArray(pend) ? pend : []).filter(
        (p: any) => p?.pacienteId === pacienteId && p?.estado === 'PENDIENTE' && p?.id,
      );
      await Promise.all(
        delPaciente.map((p: any) => Hospital.omitir(p.id, 'Alta hospitalaria').catch(() => {})),
      );
    } catch { /* best-effort */ }
  }

  async function darAlta(i: Ingreso) {
    if (!i.id) { error = 'No se pudo identificar el ingreso. Recargue la lista.'; return; }
    if (!confirm(`¿Dar de alta a ${etiquetaVisible(nombresPac, i.pacienteId, 'este paciente')}?`)) return;
    try {
      await Ingresos.alta(i.id);
      if (i.pacienteId) await limpiarKardexTrasAlta(i.pacienteId);
      await cargar();
      info = 'Alta registrada. Dosis pendientes del kárdex omitidas.';
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  onMount(() => { void cargar(); });

  const camasDisponibles = $derived(camas.filter(c => c.estado === 'DISPONIBLE'));
</script>

<section>
  <header class="view-header">
    <div>
      <h2>Ingresos activos</h2>
      <p class="muted">Admisión de pacientes y asignación de cama</p>
      <HelpHint title="Guía rápida · Ingresos">
        <ol>
          <li>Busque al paciente por nombre o CUI.</li>
          <li>Elija una cama disponible (se muestra número y servicio).</li>
          <li>El motivo se completa desde la ficha clínica si ya existe.</li>
        </ol>
      </HelpHint>
    </div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>

  {#if error}<p class="err">⚠ {error}</p>{/if}
  {#if info}<p class="ok">{info}</p>{/if}

  <form onsubmit={crear} class="row">
    {#key pickerKey}
      <PacientePicker bind:value={nuevo.pacienteId} onPick={onPickPaciente} />
    {/key}
    <label class="field"><span>Cama</span>
      <select bind:value={nuevo.camaId} required>
        <option value="">— Cama —</option>
        {#each camasDisponibles as c}
          <option value={c.id}>{c.numero} {c.servicio ? '· ' + c.servicio : ''}</option>
        {/each}
      </select>
    </label>
    <label class="field grow"><span>Motivo / diagnóstico de ingreso</span><input placeholder="Se completa desde la ficha del paciente si existe" bind:value={nuevo.diagnosticoIngreso} /></label>
    <button type="submit" disabled={!nuevo.camaId || !nuevo.pacienteId}>Ingresar</button>
  </form>

  <table>
    <thead><tr><th>Paciente</th><th>Cama</th><th>Motivo</th><th>Desde</th><th></th></tr></thead>
    <tbody>
      {#each items as i (i.id)}
        <tr>
          <td>{etiquetaVisible(nombresPac, i.pacienteId)}</td>
          <td>{etiquetaVisible(etiquetasCama, i.camaId)}</td>
          <td>{i.diagnosticoIngreso ?? i.motivoIngreso ?? '—'}</td>
          <td>{i.ingresadoEn ? new Date(i.ingresadoEn).toLocaleString() : ''}</td>
          <td><button onclick={() => darAlta(i)}>Dar alta</button></td>
        </tr>
      {:else}
        <tr><td colspan="5" class="empty">Sin ingresos activos.</td></tr>
      {/each}
    </tbody>
  </table>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; margin: 1rem 0; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field.grow { flex: 1; min-width: 200px; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .row input, .row select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .row input:focus, .row select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .55rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .92rem; color: #111827; }
  td button { padding: .25rem .55rem; border-radius: 6px; border: 1px solid #dc2626; background: #fff; color: #dc2626; cursor: pointer; font-size: .8rem; box-shadow: none; font-weight: 600; }
  td button:hover { background: #fee2e2; transform: none; }
  .err { color: #dc2626; font-weight: 600; }
  .ok { color: #166534; font-weight: 600; }
  .empty { color: #9ca3af; font-style: italic; text-align: center; padding: 1rem; }
</style>
